/**
 * Tournament progress and state computation utilities
 */

/**
 * Compute the current setup step for a tournament
 * @param {Object} tournament - Tournament object
 * @param {Array} teams - Array of teams/entries
 * @param {Array} groups - Array of groups
 * @param {Array} matches - Array of matches
 * @returns {number} Current step (1-5)
 */
export const getCurrentTournamentStep = (tournament, teams = [], groups = [], matches = []) => {
  if (!tournament) return 1;

  const status = tournament.status;
  const hasTeams = teams.length >= 4;
  const hasGroups = groups.length > 0;
  const hasGroupMatches = matches.some(m => m.round === 'group');
  const hasKnockoutMatches = matches.some(m => m.round !== 'group');

  if (status === 'completed' || status === 'cancelled') return 5;
  if (status === 'knockout' || hasKnockoutMatches) return 4;
  if (status === 'group_stage' || hasGroupMatches) return 4;
  if (hasGroups) return 3;
  if (hasTeams) return 2;
  return 1;
};

/**
 * Get tournament step definitions
 * @param {Object} tournament
 * @param {Array} teams
 * @param {Array} groups
 * @param {Array} matches
 * @returns {Array} Array of step objects
 */
export const getTournamentSteps = (tournament, teams = [], groups = [], matches = []) => {
  const hasTeams = teams.length >= 4;
  const hasGroups = groups.length > 0;
  const hasGroupMatches = matches.some(m => m.round === 'group');
  const hasKnockoutMatches = matches.some(m => m.round !== 'group');
  const isCompleted = tournament?.status === 'completed';
  const isCancelled = tournament?.status === 'cancelled';

  return [
    {
      number: 1,
      title: 'Add Teams',
      description: `Add at least 4 ${tournament?.game_type_id === 7 || tournament?.game_type_id === 5 ? 'players' : 'teams'}`,
      completed: hasTeams,
      badge: teams.length,
      action: 'teams'
    },
    {
      number: 2,
      title: 'Configure Groups',
      description: 'Set up groups and assign teams',
      completed: hasGroups,
      disabled: !hasTeams,
      badge: groups.length || null,
      action: 'group-stage'
    },
    {
      number: 3,
      title: 'Generate Matches',
      description: 'Create group stage fixtures',
      completed: hasGroupMatches,
      disabled: !hasGroups,
      action: 'group-stage'
    },
    {
      number: 4,
      title: 'Run Tournament',
      description: 'Enter scores and track progress',
      completed: isCompleted,
      disabled: !hasGroupMatches,
      badge: hasKnockoutMatches ? 'Knockout' : hasGroupMatches ? 'Groups' : null,
      action: hasKnockoutMatches ? 'knockout' : 'group-stage'
    },
    {
      number: 5,
      title: isCompleted ? 'Completed' : isCancelled ? 'Cancelled' : 'Crown Winner',
      description: isCompleted ? 'Tournament finished' : isCancelled ? 'Tournament cancelled' : 'Award the championship',
      completed: isCompleted || isCancelled,
      disabled: !hasGroupMatches
    }
  ];
};

/**
 * Calculate tournament completion percentage
 * @param {Array} matches - All tournament matches
 * @returns {number} Percentage complete (0-100)
 */
export const getTournamentCompletionPercentage = (matches = []) => {
  if (matches.length === 0) return 0;
  
  const completedMatches = matches.filter(m => 
    ['completed', 'walkover', 'forfeit'].includes(m.status) ||
    (m.games && m.games.length > 0)
  );
  
  return Math.round((completedMatches.length / matches.length) * 100);
};

/**
 * Get action items for tournament host
 * @param {Object} tournament
 * @param {Array} teams
 * @param {Array} groups
 * @param {Array} matches
 * @param {Array} groupStandings
 * @returns {Array} Array of action objects
 */
export const getTournamentActionItems = (tournament, teams = [], groups = [], matches = [], groupStandings = []) => {
  const actions = [];
  const status = tournament?.status;
  
  // Not enough teams
  if (teams.length < 4) {
    actions.push({
      type: 'warning',
      title: 'Add More Teams',
      description: `You need at least 4 ${tournament?.game_type_id === 7 || tournament?.game_type_id === 5 ? 'players' : 'teams'} to start. Currently: ${teams.length}`,
      action: 'teams',
      priority: 1
    });
  }
  
  // Solo players need pairing (doubles only)
  const soloPlayers = (tournament?.tournament_solo_entries || []).filter(s => !s.paired_at);
  if (soloPlayers.length > 0 && !(tournament?.game_type_id === 7 || tournament?.game_type_id === 5)) {
    actions.push({
      type: 'info',
      title: 'Players Without Partners',
      description: `${soloPlayers.length} player${soloPlayers.length > 1 ? 's' : ''} still need${soloPlayers.length === 1 ? 's' : ''} a partner. Pair them or add more players.`,
      action: 'teams',
      priority: 2
    });
  }
  
  // No groups configured
  if (teams.length >= 4 && groups.length === 0 && status === 'draft') {
    actions.push({
      type: 'warning',
      title: 'Configure Groups',
      description: 'Set up groups to organize the tournament',
      action: 'group-stage',
      priority: 1
    });
  }
  
  // Unassigned teams
  const assignedTeamIds = new Set();
  groups.forEach(g => {
    (g.tournament_group_entries || []).forEach(e => assignedTeamIds.add(e.entry_id));
  });
  const unassignedCount = teams.length - assignedTeamIds.size;
  if (groups.length > 0 && unassignedCount > 0) {
    actions.push({
      type: 'warning',
      title: 'Unassigned Teams',
      description: `${unassignedCount} ${tournament?.game_type_id === 7 || tournament?.game_type_id === 5 ? 'player' : 'team'}${unassignedCount > 1 ? 's' : ''} not assigned to any group`,
      action: 'group-stage',
      priority: 1
    });
  }
  
  // No matches generated
  const hasGroupMatches = matches.some(m => m.round === 'group');
  if (groups.length > 0 && !hasGroupMatches) {
    actions.push({
      type: 'warning',
      title: 'Generate Matches',
      description: 'Create group stage fixtures to begin',
      action: 'group-stage',
      priority: 1
    });
  }
  
  // Pending matches to score
  const pendingMatches = matches.filter(m => 
    m.status === 'scheduled' && 
    (!m.games || m.games.length === 0)
  );
  if (pendingMatches.length > 0 && (status === 'group_stage' || status === 'knockout')) {
    const groupPending = pendingMatches.filter(m => m.round === 'group').length;
    const knockoutPending = pendingMatches.filter(m => m.round !== 'group').length;
    
    if (groupPending > 0) {
      actions.push({
        type: 'info',
        title: 'Group Matches Pending',
        description: `${groupPending} match${groupPending > 1 ? 'es' : ''} awaiting scores`,
        action: 'group-stage',
        priority: 2
      });
    }
    
    if (knockoutPending > 0) {
      actions.push({
        type: 'info',
        title: 'Knockout Matches Pending',
        description: `${knockoutPending} match${knockoutPending > 1 ? 'es' : ''} awaiting scores`,
        action: 'knockout',
        priority: 2
      });
    }
  }
  
  // Group stage complete, ready for knockout
  const allGroupMatchesComplete = matches
    .filter(m => m.round === 'group')
    .every(m => ['completed', 'walkover', 'forfeit'].includes(m.status) || (m.games && m.games.length > 0));
  const hasKnockout = matches.some(m => m.round !== 'group');
  
  if (hasGroupMatches && allGroupMatchesComplete && !hasKnockout && status === 'group_stage') {
    actions.push({
      type: 'success',
      title: 'Ready for Knockout',
      description: 'All group matches complete. Generate the knockout bracket!',
      action: 'knockout',
      priority: 1
    });
  }
  
  return actions.sort((a, b) => a.priority - b.priority);
};

/**
 * Get tournament statistics
 * @param {Object} tournament
 * @param {Array} teams
 * @param {Array} matches
 * @returns {Object} Statistics object
 */
export const getTournamentStatistics = (tournament, teams = [], matches = []) => {
  const completedMatches = matches.filter(m => 
    ['completed', 'walkover', 'forfeit'].includes(m.status) ||
    (m.games && m.games.length > 0)
  );
  
  const groupMatches = matches.filter(m => m.round === 'group');
  const knockoutMatches = matches.filter(m => m.round !== 'group');
  
  const totalGamesPlayed = matches.reduce((sum, m) => sum + (m.games?.length || 0), 0);
  
  return {
    totalTeams: teams.length,
    totalMatches: matches.length,
    completedMatches: completedMatches.length,
    pendingMatches: matches.length - completedMatches.length,
    completionPercentage: getTournamentCompletionPercentage(matches),
    groupMatches: {
      total: groupMatches.length,
      completed: groupMatches.filter(m => 
        ['completed', 'walkover', 'forfeit'].includes(m.status) || (m.games && m.games.length > 0)
      ).length
    },
    knockoutMatches: {
      total: knockoutMatches.length,
      completed: knockoutMatches.filter(m => 
        ['completed', 'walkover', 'forfeit'].includes(m.status) || (m.games && m.games.length > 0)
      ).length
    },
    totalGamesPlayed
  };
};

/**
 * Find user's team in tournament
 * @param {string} userId
 * @param {Array} teams
 * @returns {Object|null} Team object or null
 */
export const findUserTeam = (userId, teams = []) => {
  if (!userId) return null;
  
  return teams.find(team => 
    team.player1?.id === userId || 
    team.player2?.id === userId
  ) || null;
};

/**
 * Get user's tournament progress
 * @param {string} userId
 * @param {Array} teams
 * @param {Array} matches
 * @param {Array} groupStandings
 * @returns {Object|null} Progress object or null
 */
export const getUserTournamentProgress = (userId, teams = [], matches = [], groupStandings = []) => {
  const userTeam = findUserTeam(userId, teams);
  if (!userTeam) return null;
  
  // Find team's matches
  const teamMatches = matches.filter(m => 
    m.entry1_id === userTeam.id || m.entry2_id === userTeam.id
  );
  
  const completedMatches = teamMatches.filter(m => 
    ['completed', 'walkover', 'forfeit'].includes(m.status) || (m.games && m.games.length > 0)
  );
  
  const wins = completedMatches.filter(m => m.winner_entry_id === userTeam.id).length;
  const losses = completedMatches.length - wins;
  
  // Find next match
  const nextMatch = teamMatches.find(m => 
    m.status === 'scheduled' && (!m.games || m.games.length === 0)
  );
  
  // Find group standing
  let groupStanding = null;
  for (const group of groupStandings) {
    const standing = group.standings?.find(s => s.teamId === userTeam.id);
    if (standing) {
      groupStanding = {
        groupName: group.groupName,
        position: standing.position,
        ...standing
      };
      break;
    }
  }
  
  return {
    team: userTeam,
    matches: teamMatches.length,
    completed: completedMatches.length,
    wins,
    losses,
    nextMatch,
    groupStanding
  };
};
